/**
 * Bing dwell pings.
 *
 * When server-to-server impressions are off, PAPI publishes
 * `page_options.bing_instrumentation` (search ads) and
 * `page_options.bing_shopping_instrumentation` (shopping). Each block has its
 * own `page_load_ping_url` and `ping_url_base`. This module fires those from
 * the browser, POSTs each block's impression payload when both
 * `impression_url` and `impression_payload` are present, and GETs
 * `visibility_url` when the page unloads or the tab is hidden, with the
 * tokens that entered the viewport.
 * Search and shopping stay separate.
 *
 * `clarity_id`, `property_id`, and `rguid` live on those same blocks. A
 * shopping page (`requestCategory=shopping`) reads them from
 * `bing_shopping_instrumentation`. Every other page reads
 * `bing_instrumentation`. Clarity loads from the ramp host at
 * `/cl/tag/{id}?rp=1` when `clarity_id` is set. That proxy, not `clarity.ms`,
 * is what Bing expects. Full consent queues `consentv2` granted on the
 * Clarity stub before the tag is inserted; limited consent queues denied the
 * same way, so the tag sees it at startup. When `rguid`
 * is set and a Bing ad suffix is in the DOM, telemetry loads `telemetryJS.js`
 * and calls `initTelemetryJS` on load.
 * Limited mode still loads telemetry. It does not dwell, post impressions,
 * or send visibility.
 *
 * Click logging for System1 stays on `data-beacon` / `setupClickTracking`.
 * This listener does not fire that beacon.
 *
 * When `page_options.bing_tracking.dwell_time_load_pingback` is set, a Bing
 * ad click arms one focus listener. On focus the module GETs that pingback
 * and the callback requests the stored `page_load_ping_url`. The browser
 * return stays off in that case, so Bing does not get two return pings. The
 * module also turns on when that pingback is the only Bing block. Limited
 * mode does not send it.
 */

import type { Response } from "@ascent/papi-core";
import { IN_VIEW_THRESHOLD, observeInView } from "../../tracking-events";
import { registerTrackingPartner } from "../../tracking-registry";
import { onDomReady } from "../../util";
import type { TrackingModule } from "../tracking-module";

const INSTRUMENTATION_KEYS = [
  "bing_instrumentation",
  "bing_shopping_instrumentation",
] as const;

type InstrumentationKey = (typeof INSTRUMENTATION_KEYS)[number];

interface InstrumentationBlock {
  ping_url_base?: unknown;
  page_load_ping_url?: unknown;
  impression_url?: unknown;
  impression_payload?: unknown;
  visibility_url?: unknown;
  clarity_id?: unknown;
  property_id?: unknown;
  rguid?: unknown;
}

interface VisibilityEntry {
  element: HTMLElement;
  token: string;
}

interface VisibilityWatch {
  url: string;
  pending: Set<string>;
  sent: Set<string>;
  entries: VisibilityEntry[];
}

const CLARITY_FLAG = "__RAMPJS_BING_CLARITY__";
const TELEMETRY_FLAG = "__RAMPJS_BING_TELEMETRY__";
const TELEMETRY_SRC = "https://msadsscale.microsoft.com/bingads/telemetryJS.js";
/**
 * Classic's page policy. Bing pings send the full SERP URL as the Referer
 * instead of the origin that the page's `strict-origin-when-cross-origin`
 * allows.
 */
const BING_REFERRER_POLICY: ReferrerPolicy = "no-referrer-when-downgrade";

interface TelemetryParams {
  requestGUID: string;
  clarityProjectId: string;
  propertyId: string;
}

interface TelemetryHost extends Window {
  initTelemetryJS?: (params: TelemetryParams) => void;
  [TELEMETRY_FLAG]?: boolean;
}

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

interface ClarityHost extends Window {
  clarity?: ClarityFn;
  [CLARITY_FLAG]?: boolean;
}

let pendingFocus: (() => void) | undefined;
const visibilityWatches: VisibilityWatch[] = [];
let visibilityBound = false;
const dwellBound = new WeakSet<HTMLElement>();
const visibilitySeen = new WeakSet<HTMLElement>();
const returnBound = new WeakSet<HTMLElement>();

function pageOptions(response: Response): Record<string, unknown> | undefined {
  const options = response?.page_options;
  if (!options || typeof options !== "object") return undefined;
  return options as Record<string, unknown>;
}

function instrumentationBlock(
  response: Response,
  key: InstrumentationKey,
): InstrumentationBlock | undefined {
  const value = pageOptions(response)?.[key];
  if (!value || typeof value !== "object") return undefined;
  return value as InstrumentationBlock;
}

function urlField(value: unknown): string {
  return typeof value === "string" ? value : "";
}

/**
 * Shopping tab. The renderer trims and lowercases `requestCategory` before
 * choosing the vertical, so `Shopping` is the same page. Web, news, images,
 * and the rest use the search block.
 */
function isShoppingPage(): boolean {
  const category = new URLSearchParams(window.location.search).get(
    "requestCategory",
  );
  return category?.trim().toLowerCase() === "shopping";
}

function identityKey(): InstrumentationKey {
  return isShoppingPage()
    ? "bing_shopping_instrumentation"
    : "bing_instrumentation";
}

function identityField(
  response: Response,
  read: (block: InstrumentationBlock) => unknown,
): unknown {
  const block = instrumentationBlock(response, identityKey());
  if (!block) return undefined;
  const value = read(block);
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return undefined;
}

function clarityIdOf(response: Response): string {
  const value = identityField(response, (block) => block.clarity_id);
  return typeof value === "string" ? value : "";
}

function rguidOf(response: Response): string {
  const value = identityField(response, (block) => block.rguid);
  return typeof value === "string" ? value : "";
}

function propertyIdOf(response: Response): string {
  return propertyId(identityField(response, (block) => block.property_id));
}

function installClarityStub(target: ClarityHost): void {
  if (typeof target.clarity === "function") return;
  const clarity: ClarityFn = (...args: unknown[]) => {
    clarity.q = clarity.q ?? [];
    clarity.q.push(args);
  };
  target.clarity = clarity;
}

/**
 * Origin of the ramp.js script. `/cl` is served by that host, so the tag has
 * to load from there even when the page itself is the SERP origin.
 */
function rampScriptOrigin(): string {
  const sources: string[] = [];
  const current = document.currentScript;
  if (current instanceof HTMLScriptElement && current.src) {
    sources.push(current.src);
  }
  for (const script of document.querySelectorAll<HTMLScriptElement>(
    "script[src]",
  )) {
    if (script.src) sources.push(script.src);
  }
  for (const src of sources) {
    try {
      const url = new URL(src, window.location.href);
      if (
        url.pathname.endsWith("/ramp.js") ||
        url.pathname.endsWith("/ramp.debug.js")
      ) {
        return url.origin;
      }
    } catch {
      // Keep looking.
    }
  }
  return "";
}

/**
 * One Clarity script per page. `?rp=1` tells Clarity to call `/cl` on this
 * host for the script, collect, and report requests. Calls go through the
 * `window.clarity` queue so they are kept if the tag has not finished loading.
 * `consentv2`, and `RGUID` when `rguid` is present, are queued on that stub
 * before the script is inserted. The tag runs before its load event, so a
 * load handler would apply denied consent too late.
 */
function startClarity(response: Response, granted: boolean): void {
  const clarityId = clarityIdOf(response);
  if (!clarityId) return;

  const target = window as ClarityHost;
  if (target[CLARITY_FLAG]) return;
  target[CLARITY_FLAG] = true;

  installClarityStub(target);
  const consent = granted ? "granted" : "denied";
  target.clarity?.("consentv2", {
    ad_Storage: consent,
    analytics_Storage: consent,
  });
  const requestGuid = rguidOf(response);
  if (requestGuid) target.clarity?.("set", "RGUID", requestGuid);

  const path = `/cl/tag/${encodeURIComponent(clarityId)}?rp=1`;
  const origin = rampScriptOrigin();
  const script = document.createElement("script");
  script.async = true;
  script.src = origin ? `${origin}${path}` : path;

  const first = document.getElementsByTagName("script")[0];
  if (first?.parentNode) first.parentNode.insertBefore(script, first);
  else (document.head ?? document.documentElement).appendChild(script);
}

function propertyId(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

/**
 * One telemetry script per page, and only when a Bing ad suffix is in the DOM.
 * Consent does not gate this. `initTelemetryJS` runs on script load.
 */
function startTelemetry(response: Response): void {
  const requestGuid = rguidOf(response);
  if (!requestGuid) return;

  const clarityProjectId = clarityIdOf(response);
  const property = propertyIdOf(response);

  onDomReady(() => {
    const target = window as TelemetryHost;
    if (target[TELEMETRY_FLAG]) return;
    const hasSuffix = [
      ...document.querySelectorAll("[data-bing-ping-suffix]"),
    ].some((element) => element.getAttribute("data-bing-ping-suffix"));
    if (!hasSuffix) return;
    target[TELEMETRY_FLAG] = true;

    const script = document.createElement("script");
    script.async = true;
    script.src = TELEMETRY_SRC;
    script.addEventListener("load", () => {
      target.initTelemetryJS?.({
        requestGUID: requestGuid,
        clarityProjectId,
        propertyId: property,
      });
    });
    const first = document.getElementsByTagName("script")[0];
    if (first?.parentNode) first.parentNode.insertBefore(script, first);
    else (document.head ?? document.documentElement).appendChild(script);
  });
}

/**
 * POST the URL with an empty body and the user's cookies: the request
 * `sendBeacon` makes, matching the old browser dwell path. `sendBeacon` can't
 * set a referrer policy, so this is a keepalive fetch. A thrown or rejected
 * fetch falls back to an image GET.
 */
function ping(url: string): void {
  if (!url) return;
  const fallback = (): void => {
    const image = new Image();
    image.referrerPolicy = BING_REFERRER_POLICY;
    image.src = url;
  };
  try {
    fetch(url, {
      method: "POST",
      mode: "no-cors",
      keepalive: true,
      credentials: "include",
      referrerPolicy: BING_REFERRER_POLICY,
    }).catch(fallback);
  } catch {
    fallback();
  }
}

function clearPendingFocus(): void {
  if (!pendingFocus) return;
  window.removeEventListener("focus", pendingFocus);
  pendingFocus = undefined;
}

function armReturnPing(fire: () => void): void {
  clearPendingFocus();
  const onFocus = (): void => {
    window.removeEventListener("focus", onFocus);
    if (pendingFocus === onFocus) pendingFocus = undefined;
    window.setTimeout(fire, 300);
  };
  pendingFocus = onFocus;
  window.addEventListener("focus", onFocus);
}

/**
 * GET the callback URL. `sendBeacon` would POST it. A failed GET falls back
 * to an image, which is also a GET.
 */
function getPingback(url: string): void {
  if (!url) return;
  fetch(url, { method: "GET", mode: "no-cors", keepalive: true }).catch(() => {
    const image = new Image();
    image.src = url;
  });
}

function dwellTimeLoadPingbackUrl(response: Response): string {
  const tracking = pageOptions(response)?.bing_tracking;
  if (!tracking || typeof tracking !== "object") return "";
  const pingback = (tracking as { dwell_time_load_pingback?: unknown })
    .dwell_time_load_pingback;
  if (!pingback || typeof pingback !== "object") return "";
  const record = pingback as { url?: unknown; params?: unknown };
  if (typeof record.url !== "string" || !record.url) return "";
  try {
    const url = new URL(record.url);
    if (record.params && typeof record.params === "object") {
      for (const [key, value] of Object.entries(record.params)) {
        if (typeof value !== "string" || value === "") continue;
        url.searchParams.set(key, value);
      }
    }
    return url.toString();
  } catch {
    return "";
  }
}

function bindAdActivation(anchor: HTMLElement, run: () => void): void {
  if (prefersClick()) {
    anchor.addEventListener("click", () => run());
    return;
  }
  anchor.addEventListener("mousedown", (event) => {
    if ((event as MouseEvent).buttons !== 1) return;
    run();
  });
  anchor.addEventListener("click", (event) => {
    if ((event as MouseEvent).detail !== 0) return;
    run();
  });
}

function prefersClick(): boolean {
  try {
    return window.matchMedia("(hover: none)").matches;
  } catch {
    return false;
  }
}

/**
 * Cross-origin JSON POST. A failed preflight stays a failed POST.
 */
function postImpression(block: InstrumentationBlock): void {
  const url = urlField(block.impression_url);
  const payload = block.impression_payload;
  if (!url || !payload || typeof payload !== "object") return;

  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json; charset=utf-8" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Leave the failed POST as a failed POST.
  });
}

function visibilityUrlWithTids(
  visibilityUrl: string,
  tokens: string[],
): string | undefined {
  try {
    const url = new URL(visibilityUrl);
    url.searchParams.set("tids", tokens.join(","));
    return url.toString();
  } catch {
    return undefined;
  }
}

/**
 * GET `visibility_url` with `tids`. DevTools keeps `visibilityState` at
 * `visible` across a tab switch, so a hide-only listener never runs there.
 * `beforeunload` does run on reload, and the document is still visible, so an
 * image GET is recorded in the Network panel. A hidden document drops that
 * image. `sendBeacon` would leave, but it always POSTs, so the hidden path
 * is a keepalive GET.
 */
function getVisibility(url: string): void {
  if (!url) return;
  if (document.visibilityState !== "hidden") {
    const image = new Image();
    image.referrerPolicy = BING_REFERRER_POLICY;
    image.src = url;
    return;
  }
  fetch(url, {
    method: "GET",
    keepalive: true,
    referrerPolicy: BING_REFERRER_POLICY,
  }).catch(() => {
    // Leave the failed GET. An image is dropped while the document is hidden.
  });
}

function isHalfInViewport(element: HTMLElement): boolean {
  const rect = element.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;
  const visibleWidth =
    Math.min(rect.right, viewportWidth) - Math.max(rect.left, 0);
  const visibleHeight =
    Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0);
  if (visibleWidth <= 0 || visibleHeight <= 0) return false;
  return (
    (visibleWidth * visibleHeight) / (rect.width * rect.height) >=
    IN_VIEW_THRESHOLD
  );
}

/**
 * Send tokens viewed since the last flush. Observers stay armed, so a later
 * hide can report ads that were not on screen the first time. Tokens already
 * sent are left out.
 */
function flushVisibility(): void {
  for (const watch of visibilityWatches) {
    for (const entry of watch.entries) {
      if (watch.sent.has(entry.token)) continue;
      if (isHalfInViewport(entry.element)) watch.pending.add(entry.token);
    }
    const fresh = [...watch.pending].filter((token) => !watch.sent.has(token));
    if (fresh.length === 0) continue;
    for (const token of fresh) watch.sent.add(token);
    watch.pending.clear();
    const url = visibilityUrlWithTids(watch.url, fresh);
    if (url) getVisibility(url);
  }
}

function onPageHidden(): void {
  if (document.visibilityState === "hidden") flushVisibility();
}

function bindVisibilityFlush(): void {
  if (visibilityBound) return;
  visibilityBound = true;
  document.addEventListener("visibilitychange", onPageHidden);
  window.addEventListener("pagehide", flushVisibility);
  window.addEventListener("beforeunload", flushVisibility);
}

function startVisibility(response: Response): void {
  onDomReady(() => {
    for (const key of INSTRUMENTATION_KEYS) {
      const block = instrumentationBlock(response, key);
      const visibilityUrl = urlField(block?.visibility_url);
      if (!visibilityUrl) continue;

      const watch: VisibilityWatch = {
        url: visibilityUrl,
        pending: new Set(),
        sent: new Set(),
        entries: [],
      };
      for (const element of document.querySelectorAll<HTMLElement>(
        `[data-bing-impression-token][data-bing-instrumentation="${key}"]`,
      )) {
        const token = element.getAttribute("data-bing-impression-token") ?? "";
        if (!token || visibilitySeen.has(element)) continue;
        visibilitySeen.add(element);
        watch.entries.push({ element, token });
        observeInView(element, () => {
          if (!watch.sent.has(token)) watch.pending.add(token);
        });
      }
      if (watch.entries.length === 0) continue;
      visibilityWatches.push(watch);
    }
    if (visibilityWatches.length > 0) bindVisibilityFlush();
  });
}

function postImpressions(response: Response): void {
  for (const key of INSTRUMENTATION_KEYS) {
    const block = instrumentationBlock(response, key);
    if (block) postImpression(block);
  }
}

function setupBlock(response: Response, key: InstrumentationKey): void {
  const block = instrumentationBlock(response, key);
  if (!block) return;

  const anchors = [
    ...document.querySelectorAll<HTMLElement>(
      `[data-bing-ping-suffix][data-bing-instrumentation="${key}"]`,
    ),
  ].filter(
    (element) =>
      element.getAttribute("data-bing-ping-suffix") && !dwellBound.has(element),
  );
  if (anchors.length === 0) return;

  const pageLoadUrl = urlField(block.page_load_ping_url);
  const pingUrlBase = urlField(block.ping_url_base);
  const serverReturn = dwellTimeLoadPingbackUrl(response);
  ping(pageLoadUrl);

  const send = (anchor: HTMLElement): void => {
    const suffix = anchor.getAttribute("data-bing-ping-suffix") ?? "";
    if (!suffix || !pingUrlBase) return;
    // Raw concatenation. Do not encode the suffix.
    ping(pingUrlBase + suffix);
    // The pingback return is armed separately. Arming both sends two page-load pings.
    if (!serverReturn && pageLoadUrl) armReturnPing(() => ping(pageLoadUrl));
  };

  for (const anchor of anchors) {
    dwellBound.add(anchor);
    bindAdActivation(anchor, () => send(anchor));
  }
}

/**
 * Arm the callback return ping from the ad click. This is not the
 * `ping_url_base` handler: that path pings Bing directly and does not run
 * when server-side impressions omit `ping_url_base`.
 */
function startReturnPing(response: Response): void {
  const url = dwellTimeLoadPingbackUrl(response);
  if (!url) return;

  onDomReady(() => {
    const anchors = [
      ...document.querySelectorAll<HTMLElement>(
        "[data-bing-ping-suffix][data-bing-instrumentation]",
      ),
    ].filter((element) => element.getAttribute("data-bing-ping-suffix"));
    for (const anchor of anchors) {
      if (returnBound.has(anchor)) continue;
      returnBound.add(anchor);
      bindAdActivation(anchor, () => armReturnPing(() => getPingback(url)));
    }
  });
}

function startDwell(response: Response): void {
  onDomReady(() => {
    for (const key of INSTRUMENTATION_KEYS) {
      setupBlock(response, key);
    }
  });
}

export const BingModule: TrackingModule = {
  id: "bing",
  name: "Bing Clarity, dwell, and telemetry",
  requiresExplicitConsent: false,

  isEnabled: (response: Response): boolean => {
    const options = pageOptions(response);
    if (!options) return false;
    if (
      INSTRUMENTATION_KEYS.some((key) => {
        const value = options[key];
        return !!value && typeof value === "object";
      })
    ) {
      return true;
    }
    return dwellTimeLoadPingbackUrl(response) !== "";
  },

  initialize: (response: Response): void => {
    startClarity(response, true);
    startTelemetry(response);
    postImpressions(response);
    startDwell(response);
    startVisibility(response);
    startReturnPing(response);
  },

  /**
   * Consent opt-out. Clarity still loads, with `consentv2` denied queued
   * before the tag starts, and telemetry still loads. Dwell, the return
   * ping, impressions, and visibility stay off.
   */
  initializeLimited: (response: Response): void => {
    startClarity(response, false);
    startTelemetry(response);
  },
};

registerTrackingPartner(BingModule);

export { BingModule as default };

/**
 * Bind dwell, visibility, telemetry, and the return ping to Bing markup
 * currently in the document. A later call, after deferred units land, binds
 * only the new anchors. Page-load pings fire for a block only when it gained
 * anchors. Clarity and impression posts stay on `BingModule.initialize` so
 * they still run once.
 */
export function bindBingInstrumentation(response: Response): void {
  startTelemetry(response);
  startDwell(response);
  startVisibility(response);
  startReturnPing(response);
}

/** Telemetry only. Limited consent still loads it and leaves dwell off. */
export function bindBingTelemetry(response: Response): void {
  startTelemetry(response);
}
